// CFD-side subscriber state.
//
// The CFD window opens, asks POS for current cart state via
// `requestCartState()`, then subscribes to `cart.updated` / `cart.cleared`
// frames as POS broadcasts them. This file exposes a tiny store + hook
// the route component reads -- it does NOT use cartStore (cartStore is
// the POS source of truth; the CFD is read-only mirror).
//
// Why a separate store: the CFD process can run in a different window
// than POS (and may be a sibling Capacitor instance / second tab). It
// has no business writing to cartStore; its only input is the channel.

import { useSyncExternalStore } from 'react'
import type { CartSnapshot, FeaturedProductPayload } from './cartChannel'
import {
  onCartChannelMessage,
  requestCartState,
} from './cartChannel'

interface CfdState {
  /** Latest cart snapshot from POS, or null if cart is empty / not connected. */
  snapshot: CartSnapshot | null
  /** Currently-featured product on the right panel, set by `product.shown`
   *  and cleared by `product.hidden`. Null when the cashier doesn't have a
   *  product modal open. */
  featuredProduct: FeaturedProductPayload | null
  /** Wall-clock ms when the last frame arrived. Used to gate "stale" UI
   *  if POS goes silent (e.g. clerk closes their window). */
  lastReceivedAt: number | null
  /** Sequence number of the last frame applied. Drops out-of-order frames. */
  lastSeq: number | null
  /** True once the CFD has received at least one frame. Helps pick the
   *  right empty state ("waiting for register…" vs "no items in cart"). */
  hasConnected: boolean
  /** Increments each time POS broadcasts `payment.approved`. The CFD
   *  watches this in a useEffect to fire one-shot animations (confetti)
   *  without making the celebration part of persistent state. */
  paymentApprovedTick: number
}

let _state: CfdState = {
  snapshot: null,
  featuredProduct: null,
  lastReceivedAt: null,
  lastSeq: null,
  hasConnected: false,
  paymentApprovedTick: 0,
}

const _listeners = new Set<() => void>()

function emit() {
  for (const l of _listeners) l()
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

function getSnapshot(): CfdState {
  return _state
}

/**
 * Wire CFD → BroadcastChannel. Returns an unsubscribe function. Call once
 * from the route's mount effect.
 *
 * Idempotent in the sense that multiple subscriptions see the same
 * messages, but each one keeps its own local state. Typical usage is one
 * subscription per render of the CFD route.
 */
export function setupCfdSubscriber(): () => void {
  const off = onCartChannelMessage((msg) => {
    // Drop only exact-duplicate frames (some browsers fire storage events
    // twice for the same key write). We don't filter "older" seqs because
    // BroadcastChannel and storage events are in-order to a single listener
    // — and the POS-side `_seq` resets to 0 on module reload (window
    // navigation, wrapper restart, HMR), so a strict `<= lastSeq` filter
    // would silently drop every message after a reload until the new
    // counter caught up to the pre-reload value, leaving the CFD stuck on
    // the last-applied state.
    if (typeof msg.seq === 'number' && _state.lastSeq !== null) {
      if (msg.seq === _state.lastSeq) return
    }

    if (msg.type === 'cart.updated') {
      _state = {
        ..._state,
        snapshot: msg.cart ?? null,
        lastReceivedAt: msg.ts,
        lastSeq: typeof msg.seq === 'number' ? msg.seq : _state.lastSeq,
        hasConnected: true,
      }
      emit()
    } else if (msg.type === 'cart.cleared') {
      // Distinguish a real transaction-end (commit/cancel) from the
      // empty-cart heartbeat the POS fires every 5s. Both arrive as the
      // same `cart.cleared` message, but only the transaction-end should
      // wipe `featuredProduct` — the heartbeat fires while the cart is
      // already empty, which is exactly when the cashier might have a
      // product modal open to show the customer (so they can decide
      // before adding to the cart). Heuristic: a real clear transitions
      // FROM a non-empty snapshot; a heartbeat finds us already empty.
      const wasNonEmpty =
        _state.snapshot !== null && _state.snapshot.lines.length > 0
      _state = {
        ..._state,
        snapshot: null,
        // Preserve featuredProduct on heartbeat clears so an in-progress
        // product showcase doesn't blink out every 5s. The legitimate
        // clearer for the featured panel is `product.hidden` (modal
        // close), which still works.
        featuredProduct: wasNonEmpty ? null : _state.featuredProduct,
        lastReceivedAt: msg.ts,
        lastSeq: typeof msg.seq === 'number' ? msg.seq : _state.lastSeq,
        hasConnected: true,
      }
      emit()
    } else if (msg.type === 'product.shown') {
      _state = {
        ..._state,
        featuredProduct: msg.product ?? null,
        lastReceivedAt: msg.ts,
        lastSeq: typeof msg.seq === 'number' ? msg.seq : _state.lastSeq,
        hasConnected: true,
      }
      emit()
    } else if (msg.type === 'product.hidden') {
      _state = {
        ..._state,
        featuredProduct: null,
        lastReceivedAt: msg.ts,
        lastSeq: typeof msg.seq === 'number' ? msg.seq : _state.lastSeq,
        hasConnected: true,
      }
      emit()
    } else if (msg.type === 'payment.approved') {
      _state = {
        ..._state,
        paymentApprovedTick: _state.paymentApprovedTick + 1,
        lastReceivedAt: msg.ts,
        lastSeq: typeof msg.seq === 'number' ? msg.seq : _state.lastSeq,
        hasConnected: true,
      }
      emit()
    }
    // 'cart.request_state' is not consumed here -- only POS replies to
    // those, and the CFD never sends one back.
  })

  // Ask POS for the current state on subscribe so we don't render a
  // misleading "empty" state for a cart that already has items.
  requestCartState()

  return off
}

/**
 * React hook: subscribe to the CFD state and re-render on every change.
 * The effect that calls `setupCfdSubscriber` lives in the route component
 * so the subscription teardown matches the unmount.
 */
export function useCfdState(): CfdState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
